import { useEffect, useState } from 'react';
import {
	AVAILABLE_VERSION_BY_TEMPLATE_TYPE,
	FilePreviewMode,
	GeneratableDocumentVersion,
} from '@dealstack/shared/lib/common/business/generatableDocuments.ts';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getGeneratableDocumentTemplateFile } from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import type {
	DataGeneratableDocumentWorkflowTemplateFieldTct,
	GeneratableDocumentWorkflowTemplateType,
} from '@dealstack/shared/lib/generated';

export const useTemplatePreviewController = (
	smartFields: DataGeneratableDocumentWorkflowTemplateFieldTct[],
	templateType: GeneratableDocumentWorkflowTemplateType
) => {
	const previewVersions = AVAILABLE_VERSION_BY_TEMPLATE_TYPE[templateType];

	const [fileBlob, setFileBlob] = useState<Blob>();
	const [fileName, setFileName] = useState<string>();

	const [selectedVersion, setSelectedVersion] = useState<GeneratableDocumentVersion>(
		previewVersions[0]
	);
	const [filePreviewMode, setFilePreviewMode] = useState<FilePreviewMode>(
		FilePreviewMode.SMART_FIELDS
	);

	const getFileQuery = useQuery({
		queryKey: [
			API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_FILE_TYPE_GET,
			{ templateType, version: selectedVersion, viewMode: filePreviewMode },
		],
		queryFn: ({ signal }) =>
			getGeneratableDocumentTemplateFile(
				{ templateType, version: selectedVersion, viewMode: filePreviewMode },
				signal
			),
	});

	useEffect(() => {
		if (!getFileQuery.isSuccess) {
			return;
		}
		void (async () => {
			const response = getFileQuery.data;
			if (response) {
				const blob = await response.blob();
				const fileName = response.headers.get('FileName') as string;
				setFileName(fileName);
				setFileBlob(blob);
			}
		})();
	}, [getFileQuery.isSuccess, getFileQuery.data]);

	return {
		fileBlob,
		fileName,
		selectedVersion,
		setSelectedVersion,
		filePreviewMode,
		setFilePreviewMode,
		isFileLoading: getFileQuery.isFetching,
	};
};
